import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { rem } from 'design-system';
import { Avatar } from 'design-system/avatar';
import BlurAmountView from 'design-system/BlurAmountView';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import { formatDateForItem } from 'features/invest/utils';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const strings = {
    completed: 'Completed',
    rejected: 'Rejected',
    deleted: 'Deleted',
    pending: 'Pending',
};
const StatusMap = {
    completed: { icon: 'done', text: strings.completed, color: 'Positive' },
    failed: { icon: 'cancelled-red', text: strings.rejected, color: 'Negative' },
    cancelled: { icon: 'cancelled-red', text: strings.deleted, color: 'Negative' },
    pending: { icon: 'in-progress', text: strings.pending, color: 'StatusInformational' },
};
const StatusView = React.memo(({ status }) => {
    const { icon, text, color } = StatusMap[status];
    const colorProp = { [color]: true };
    return (<View style={staticStyles.status}>
      <StatusIcon state={icon} width={16} height={16}/>
      <Text TextThin-14 {...colorProp}>
        {text}
      </Text>
    </View>);
});
const ProductTransferActivityItem = ({ activity, isFirstInSection, isLastInSection, withWrapper }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const borderStyles = useMemo(() => [isFirstInSection && styles.firstItem, isLastInSection && styles.lastItem], [styles.firstItem, styles.lastItem, isFirstInSection, isLastInSection]);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const { amount, title, status, timestamp, imgUrl } = activity;
    const formattedDate = status === 'completed' || status === 'failed' ? formatDateForItem(timestamp) : undefined;
    const amountView = useMemo(() => {
        if (status === 'failed' || status === 'cancelled') {
            return (<Text Numbers-16 strikeThrough right>
          {formatAmount(amount.value, amount.currency, 2)}
        </Text>);
        }
        return (<Text Numbers-16 Blue right>
        +{formatAmount(amount.value, amount.currency, 2)}
      </Text>);
    }, [amount, status]);
    return (<View style={[styles.wrapper, withWrapper ? borderStyles : null]}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={() => {
            navigation.navigate('ProductTransferProgress', { productTransferId: activity.id });
        }} activeOpacity={1} style={withWrapper && !isWeb ? borderStyles : null} underlayColor={colors.background.underlay}>
        <View style={styles.row}>
          <Avatar size={44} name={activity.providerName} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} imageUrl={imgUrl}/>
          <View style={styles.flex}>
            <Text Text-16 numberOfLines={1} flex>
              {title}
            </Text>
            <StatusView status={status}/>
          </View>
          <View>
            <BlurAmountView enabled={scrambled}>{amountView}</BlurAmountView>
            {!!formattedDate && (<Text TextThin-14 Gray numberOfLines={1} right>
                {formattedDate}
              </Text>)}
          </View>
        </View>
      </TouchableHighlight>
    </View>);
};
const staticStyles = StyleSheet.create({
    status: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
});
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
    row: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
        // On web the card is padded by 8 and each row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    flex: {
        flex: 1,
    },
}));
export default React.memo(ProductTransferActivityItem);
